import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { findAllEntities } from 'selectors/entitySelectors'
import { getQueryValue, getParamValue } from 'selectors/pageStateSelectors'
import { STATE_KEY as SK } from 'reducers/naughtyReducers'
import { FIELD_LABELS, convertKey } from 'components/VRP/naughtyBitLookups'
import uuid from 'uuid/v1'

import {
  getRooftopId,
  getRooftopInfo,
  getTaggedCustomerFromState,
} from 'selectors/appStatusSelectors'

import { getEntity } from 'selectors/entitySelectors'

import {
  getQuoteId,
  getQuoteValue,
  getBestPayment,
} from 'selectors/quoteSelectors'
import { shouldTrack } from 'selectors/authSelectors'

import { getIsLoggedIn, getIsDealerBrowser } from 'selectors/authSelectors'

import { REACTIONS, INTENTS_STATS_KEYS } from 'reducers/quoteReducers'
import { SALES_ROOFTOPS } from 'utils'

export const ILLUMINATIONS_KEY = 'illuminations'
export const CUSTOMER_KEY = 'customer'
export const SQUEEZE_KEY = 'squeeze'

const warn = e => window.LOG_LEVEL === 'DEBUG' && console.warn(e)
const error = e => window.LOG_LEVEL === 'DEBUG' && console.error(e)

const COOKIE_NAME = 'magic-link-context'

// This is a dummy ImmortalDB in cases for iframes with 3rd party cookied
// disabled
let ImmortalDB = {
  get: () => new Promise(r => r()), // eslint-disable-line
  set: () => new Promise(r => r()), // eslint-disable-line
}

try {
  ImmortalDB = require('immortal-db').ImmortalDB
} catch (e) {
  warn('ImmortalDB failed to init: ', e)
}

// interface directly with the ImmortalDB storage
export const NaughtyDB = {
  get: async key => {
    let ret = {}

    try {
      await ImmortalDB.get(key).then(db => (ret = JSON.parse(db || '{}')))
    } catch (e) {
      warn(e)
      NaughtyDB.set(key)
    }

    return ret
  },
  getByType: async (key, type) => {
    let ret = {}

    try {
      await ImmortalDB.get(key).then(db => {
        let data = JSON.parse(db) || {}

        ret = Object.keys(data).reduce(
          (ret, k) =>
            data[k]._entityType === type ? { ...ret, [k]: data[k] } : ret,
          {},
        )
      })
    } catch (e) {
      warn(e)
      NaughtyDB.set(key)
    }

    return ret
  },
  set: async (key, data) => {
    try {
      await ImmortalDB.set(key, data ? JSON.stringify(data) : null)
    } catch (e) {
      warn(e)
    }
  },
  flush: async (key, ids = []) => {
    let newData = {}

    try {
      let data = await NaughtyDB.get(key)

      newData = isEmpty(ids)
        ? {}
        : Object.keys(data || {}).reduce(
            (ret, k) => (ids.includes(k) ? ret : { ...ret, [k]: data[k] }),
            {},
          )

      warn(
        `IDB FLUSH result: ${ids.length} flushed / ${
          Object.keys(newData).length
        } cached`,
      )
    } catch (e) {
      warn(e)
    }

    newData = isEmpty(newData) ? null : newData

    await NaughtyDB.set(key, newData)
  },
}

// magic link cookie getter to hydrate customer tag from context
export const getCookie = key => {
    let cookie = document.cookie,
      output = cookie.split(/\s*;\s*/).reduce((ret, c) => {
        let pair = c.split(/\s*=\s*/)

        return {
          ...ret,
          [pair[0]]: pair
            .splice(1)
            .join('=')
            .replaceAll('+', '%20'), // TODO safe?
        }
      }, {})

    return decodeURIComponent(output[key])
  },
  convertMagicCookie = magicCookie => {
    let {
        firstName,
        lastName,
        phoneNumber,
        emailAddress,
        postalCode,
        tradeParams = {},
      } = magicCookie,
      magicCustomer = !isEmpty(magicCookie)
        ? {
            sessionId: magicCookie.deviceId || uuid(),
            firstName,
            lastName,
            phone: phoneNumber,
            email: emailAddress,
            zipCode: postalCode,
            vehicleId: get(magicCookie, 'decodedInventoryParams.vehicleId'),
            trades: [
              {
                payoff: 0,
                allowance: 0,
                acv: 0,
                ...tradeParams,
              },
            ],
          }
        : {}

    return magicCustomer
  },
  getMagicCustomer = () => {
    let cookieData = {}

    try {
      cookieData = JSON.parse(getCookie(COOKIE_NAME))
    } catch (e) {
      error(e)
    }

    return convertMagicCookie(cookieData)
  }

window.getMagicCustomer = getMagicCustomer

// regular redux selectors for pulling out events from app state
export const getEventsForQuote = (state, qId) => {
    return findAllEntities(
      state,
      e => e._entityType === 'event' && get(e, 'payload.quoteId') === qId,
    )
  },
  getEventsForVehicle = (state, vId) => {
    return findAllEntities(
      state,
      e => e._entityType === 'event' && get(e, 'payload.vehicleId') === vId,
    )
  },
  getRooftopKey = (state, key) =>
    getRooftopId(state) ? `${getRooftopId(state)}-${key}` : null,
  getTaggedCustomer = async (state, noDealer) => {
    let customer = await NaughtyDB.get(getRooftopKey(state, CUSTOMER_KEY))
    return noDealer && customer._entityType === 'dealer' ? null : customer
  },
  getSqueezeLimit = state =>
    get(
      getRooftopInfo(state),
      'config.quote.scanOptions.[0].squeezeLimit',
      false,
    ),
  getSqueeze = async state => {
    let squeeze = await NaughtyDB.get(getRooftopKey(state, SQUEEZE_KEY))

    return squeeze
  },
  getTooSqueezed = async state => {
    let squeezeCount = parseInt(get(await getSqueeze(state), 'count', 0), 10),
      squeeze = shouldTrack(state),
      squeezeLimit = parseInt(getSqueezeLimit(state), 10),
      isKnown = await isKnownCustomer(state)

    return (
      squeeze &&
      !isNaN(squeezeLimit) &&
      !isKnown &&
      squeezeCount >= squeezeLimit
    )
  },
  getAllStats = state => get(state, `${SK}.allStats`, []),
  getSelectedDateRange = (state, def) =>
    getQueryValue(state, 'stats.dateRange', def || 'today'),
  getSelectedEventType = state => getQueryValue(state, 'stats.eventType', null),
  getTotalCreatedStats = ({ illuminationBreakdown, totalsByType }) => {
    let current = get(illuminationBreakdown, 'current.quote_created', 0)

    return {
      value: current,
      diff: current - get(illuminationBreakdown, 'previous.quote_created', 0),
      full: true,
      arrow: true,
      substats: [
        {
          values: [
            { label: 'Lease', value: get(totalsByType, 'created.lease', 0) },
            { label: 'Loan', value: get(totalsByType, 'created.loan', 0) },
            { label: 'Cash', value: get(totalsByType, 'created.cash', 0) },
          ],
        },
        {
          values: [
            { label: 'New', value: get(totalsByType, 'created.new', 0) },
            { label: 'Used', value: get(totalsByType, 'created.used', 0) },
          ],
        },
      ],
    }
  },
  getOpenedStats = ({ illuminationBreakdown }) => {
    let views = get(illuminationBreakdown, 'current.quote_viewed', 0)

    return {
      value: views,
      full: true,
      // filterKey: 'quote_viewed',
      diff: views - get(illuminationBreakdown, 'previous.quote_viewed', 0),
      substats: [
        {
          values: [
            {
              label: 'Mobile',
              value: get(illuminationBreakdown, 'current.mobile_opens', 0),
            },
            {
              label: 'Desktop',
              value: get(illuminationBreakdown, 'current.desktop_opens', 0),
            },
          ],
        },
      ],
    }
  },
  getIlluminationStats = ({
    illuminationBreakdown = [],
    topInteractions = [],
  }) => {
    let total = get(illuminationBreakdown, 'current.total_illuminations', 0)

    return {
      value: total,
      arrow: true,
      full: true,
      diff:
        total - get(illuminationBreakdown, 'previous.total_illuminations', 0),
      substats: [
        {
          //vertical: true,
          limit: 3,
          values: topInteractions.map(({ key, count }) => ({
            label: FIELD_LABELS[key] || convertKey(key, true) || key,
            value: count,
          })),
        },
      ],
    }
  },
  getAcceptedStats = ({ illuminationBreakdown, totalsByType }) => {
    let accepted = get(illuminationBreakdown, 'current.quote_accepted', 0)

    return {
      value: accepted,
      double: true,
      arrow: true,
      // filterKey: 'quote_accepted',
      diff: accepted - get(illuminationBreakdown, 'previous.quote_accepted', 0),
      substats: [
        {
          values: [
            { label: 'Lease', value: get(totalsByType, 'accepted.lease', 0) },
            { label: 'Loan', value: get(totalsByType, 'accepted.loan', 0) },
            { label: 'Cash', value: get(totalsByType, 'accepted.cash', 0) },
          ],
        },
        {
          values: [
            { label: 'New', value: get(totalsByType, 'accepted.new', 0) },
            { label: 'Used', value: get(totalsByType, 'accepted.used', 0) },
          ],
        },
      ],
    }
  },
  getEngagedStats = ({ illuminationBreakdown }) => {
    let engaged = get(illuminationBreakdown, 'current.engagements', 0)

    return {
      value: engaged,
      // filterKey: 'current.engagements',
      diff: engaged - get(illuminationBreakdown, 'previous.engagements', 0),
      arrow: true,
    }
  },
  getTotalReactions = ({ illuminationBreakdown }) => {
    let totals = get(illuminationBreakdown, 'current.total_reactions', 0)

    return {
      value: totals,
      // filterKey: 'current.total_reactions',
      diff: totals - get(illuminationBreakdown, 'previous.total_reactions', 0),
      arrow: true,
    }
  },
  getCounteredStats = ({ illuminationBreakdown }) => {
    let rejected = get(illuminationBreakdown, 'current.quote_rejected', 0)

    return {
      value: rejected,
      // filterKey: 'quote_rejected',
      diff: rejected - get(illuminationBreakdown, 'previous.quote_rejected', 0),
      arrow: true,
    }
  },
  getHotLeads = ({ illuminationBreakdown }) => {
    let hotLeads = get(illuminationBreakdown, 'current.known_customers', 0)

    return {
      value: hotLeads,
      // filterKey: 'quote_rejected',
      diff:
        hotLeads - get(illuminationBreakdown, 'previous.known_customers', 0),
      arrow: true,
    }
  },
  getHotLeadAlerts = ({ illuminationBreakdown }) => {
    let hotLeadAlerts = get(illuminationBreakdown, 'current.adfs_sent', 0)

    return {
      value: hotLeadAlerts,
      // filterKey: 'quote_rejected',
      diff: hotLeadAlerts - get(illuminationBreakdown, 'previous.adfs_sent', 0),
      arrow: true,
    }
  },
  getRawAggregateValues = state => get(state, `${SK}.aggregateStats`, []),
  getAssembledAggregates = state => {
    let raw = getRawAggregateValues(state)

    return [
      // {
      //   Opened: getOpenedStats(raw),
      // },
      // {
      //   Created: getTotalCreatedStats(raw),
      // },
      {
        Engaged: getEngagedStats(raw),
      },
      {
        Activity: getIlluminationStats(raw),
      },
      {
        Reactions: getTotalReactions(raw),
      },
      {
        'Hot Leads': getHotLeads(raw),
      },
      {
        'Hot Lead Alerts': getHotLeadAlerts(raw),
      },
    ]
  },
  getReactionStatsOld = state => {
    let tops = get(getRawAggregateValues(state), 'topInteractions', []),
      reactions = REACTIONS.reduce(
        (ret, k) => ({
          ...ret,
          [k]: get(tops.find(t => t.key === k), 'count', 0),
        }),
        {},
      )

    return reactions || {}
  },
  getReactionStats = state => {
    let currentIllums = get(
      getRawAggregateValues(state),
      'illuminationBreakdown.current',
      [],
    )

    return REACTIONS.reduce(
      (ret, rk) => ({ ...ret, [rk]: currentIllums[INTENTS_STATS_KEYS[rk]] }),
      {},
    )
  },
  getIsSalesRooftop = state => SALES_ROOFTOPS.includes(getRooftopId(state)),
  getSalesSqueeze = state => {
    let isSalesRooftop = getIsSalesRooftop(state),
      existingCustomer = getTaggedCustomerFromState(state),
      { firstName } = existingCustomer

    return shouldTrack(state) && isSalesRooftop && isEmpty(firstName)
  },
  getIlluminationsForStats = state => get(state, `${SK}.illuminations`, []),
  getMapStats = state => get(state, `${SK}.mapStats`, []) || [],
  isGettingStats = state => get(state, `${SK}.isGettingStats`, false),
  getGTMPayload = state => {
    let vehicleId = getParamValue(state, 'id'),
      quoteId = getQuoteId(state, vehicleId),
      reaction = getQueryValue(state, 'quoteAction'),
      vehicle = getEntity(state, vehicleId),
      gqv = key => getQuoteValue(state, vehicleId, quoteId, key),
      mode = gqv('mode'),
      payment = getBestPayment(state, vehicleId, quoteId, mode),
      zipcode = gqv('customerZipCode')

    return !isEmpty(quoteId) && !isEmpty(zipcode)
      ? {
          VIN: vehicle.vin,
          Year: vehicle.year,
          Make: vehicle.make,
          Model: vehicle.model,
          TransactionType: mode,
          PaymentAmount: payment,
          QuoteInterest: reaction,
          ZipCode: zipcode,
        }
      : {}
  },
  isKnownCustomer = async state => {
    let customer = await getTaggedCustomer(state)

    return (
      getIsDealerBrowser(state) ||
      !isEmpty(getIsLoggedIn(state)) ||
      !isEmpty(get(customer, 'email')) ||
      !isEmpty(get(customer, 'phone'))
    )
  },
  getImmortalPins = state => {
    let customer = getTaggedCustomerFromState(state)

    return get(customer, 'quotes', {})
  }

// TODO remove this eventually
window.flushNaughty = NaughtyDB.flush
window.flushNaughtyByType = NaughtyDB.flushByType
window.getNaughty = NaughtyDB.get
window.setNaughty = NaughtyDB.setRaw
